<!-- src/components/modal/pipeline-modal.vue -->
<template>
  <!-- Add Pipeline -->
  <div class="modal fade dq-pipeline-modal" id="add_pipeline" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-xl dq-pipeline-dialog">
      <div class="modal-content dq-modal-content" :key="addKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-violet">
              <i class="ti ti-route"></i>
            </div>
            <div class="dq-headtext">
              <h4 class="modal-title dq-title mb-0">Add New Pipeline</h4>
              <div class="dq-subtitle">
                Define stages, set access, and control visibility.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitCreate">
          <!-- Body -->
          <div class="modal-body dq-modal-body">
            <div class="row g-3">
              <!-- Pipeline basics -->
              <div class="col-12">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <i class="ti ti-sparkles"></i> Pipeline Details
                    </div>
                    <div class="dq-card-sub">
                      Give your pipeline a name and manage its stages.
                    </div>
                  </div>

                  <div class="row g-3 mt-0">
                    <div class="col-12">
                      <label class="form-label dq-label">
                        Pipeline Name <span class="text-danger">*</span>
                      </label>
                      <input
                        v-model.trim="form.name"
                        type="text"
                        class="form-control dq-input"
                        placeholder="e.g. Sales, Marketing, Support"
                        required
                      />
                    </div>

                    <!-- Stages -->
                    <div class="col-12">
                      <div
                        class="d-flex justify-content-between align-items-center mb-2"
                      >
                        <label class="form-label dq-label mb-0">
                          Pipeline Stages <span class="text-danger">*</span>
                        </label>

                        <button
                          type="button"
                          class="btn btn-outline-primary btn-sm dq-mini-btn"
                          @click="openStageModal"
                        >
                          <i class="ti ti-plus me-1"></i> Add Stage
                        </button>
                      </div>

                      <!-- Stage list -->
                      <div v-if="form.stages.length" class="dq-stage-list">
                        <div
                          v-for="(st, idx) in form.stages"
                          :key="`a-${idx}-${
                            typeof st === 'string' ? st : st.name
                          }`"
                          class="dq-stage-row"
                        >
                          <div class="dq-stage-left">
                            <span class="dq-drag"
                              ><i class="ti ti-grip-vertical"></i
                            ></span>
                            <div class="dq-stage-meta">
                              <div class="dq-stage-name">
                                {{ typeof st === "string" ? st : st.name }}
                              </div>
                              <div class="dq-stage-sub">
                                Step {{ idx + 1 }} • Sort
                                {{
                                  typeof st === "object"
                                    ? st.sort_order ?? idx
                                    : idx
                                }}
                              </div>
                            </div>
                          </div>

                          <div class="dq-stage-actions">
                            <button
                              type="button"
                              class="btn btn-light btn-sm dq-iconbtn"
                              @click="openEditStage(idx, st)"
                              title="Edit"
                            >
                              <i class="ti ti-edit"></i>
                            </button>
                            <button
                              type="button"
                              class="btn btn-light btn-sm dq-iconbtn"
                              @click="removeStage(idx)"
                              title="Remove"
                            >
                              <i class="ti ti-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div v-else class="dq-empty">
                        <i class="ti ti-info-circle"></i>
                        <div>
                          No stages yet. Click <strong>Add Stage</strong> to
                          create the first step.
                        </div>
                      </div>

                      <div v-if="!form.stages.length" class="dq-help">
                        Tip: Start with “New”, “Qualified”, “Proposal”,
                        “Won/Lost”.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Access -->
              <div class="col-12">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <i class="ti ti-eye"></i> Access
                    </div>
                    <div class="dq-card-sub">
                      Choose who can use this pipeline.
                    </div>
                  </div>

                  <div class="dq-visibility">
                    <label class="dq-radio">
                      <input
                        class="form-check-input"
                        type="radio"
                        :checked="form.is_public"
                        @change="setPublic(true)"
                      />
                      <span class="dq-radio-box">
                        <i class="ti ti-world"></i>
                        <span class="dq-radio-title">All</span>
                      </span>
                    </label>

                    <label class="dq-radio">
                      <input
                        class="form-check-input"
                        type="radio"
                        :checked="!form.is_public"
                        @change="setPublic(false)"
                      />
                      <span class="dq-radio-box">
                        <i class="ti ti-users"></i>
                        <span class="dq-radio-title">Select People</span>
                      </span>
                    </label>
                  </div>

                  <!-- Select people picker (Deals pattern) -->
                  <div v-if="!form.is_public" class="dq-restrict mt-3">
                    <div class="dq-restrict-head">
                      <div class="dq-restrict-title">
                        <i class="ti ti-user-plus"></i> Choose people
                      </div>
                      <div class="dq-restrict-sub">
                        Search and add users who can access this pipeline.
                      </div>
                    </div>

                    <!-- Selected chips -->
                    <div v-if="accessSelected.length" class="dq-selected mt-2">
                      <span class="dq-mini">Selected:</span>
                      <span
                        v-for="u in accessSelected"
                        :key="'sel-' + u.id"
                        class="dq-chip"
                      >
                        <i class="ti ti-user"></i>
                        {{ u.label }}
                        <i
                          class="ti ti-x ms-1"
                          role="button"
                          @click="removeAccessUser(u.id)"
                        ></i>
                      </span>
                    </div>

                    <!-- Dropdown -->
                    <div
                      class="dropdown w-100 mt-2"
                      data-bs-auto-close="outside"
                      data-bs-display="static"
                    >
                      <button
                        class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                        type="button"
                        data-bs-toggle="dropdown"
                        @click="openAccessDropdown"
                      >
                        <span class="text-truncate">Add people…</span>
                        <i class="ti ti-chevron-down ms-2"></i>
                      </button>

                      <div class="dropdown-menu p-2 w-100 shadow">
                        <input
                          v-model="accessQuery"
                          type="text"
                          class="form-control mb-2"
                          placeholder="Search users…"
                          autocomplete="off"
                        />

                        <div class="search-list">
                          <div
                            v-if="accessLoading"
                            class="text-center py-2 small text-muted"
                          >
                            Loading…
                          </div>

                          <button
                            v-for="opt in accessOptions"
                            :key="'acc-' + opt.id"
                            type="button"
                            class="dropdown-item d-flex justify-content-between align-items-center"
                            @click="toggleAccessUser(opt)"
                          >
                            <span class="text-truncate">{{ opt.label }}</span>
                            <i
                              class="ti"
                              :class="
                                form.user_ids.includes(opt.id)
                                  ? 'ti-check'
                                  : 'ti-user-plus'
                              "
                            ></i>
                          </button>

                          <div
                            v-if="!accessLoading && !accessOptions.length"
                            class="text-center py-2 small text-muted"
                          >
                            No results
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- /Access -->

              <!-- Status -->
              <div class="col-12">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <i class="ti ti-activity"></i> Status
                    </div>
                    <div class="dq-card-sub">
                      Enable or disable this pipeline.
                    </div>
                  </div>

                  <div class="row g-3 mt-0">
                    <div class="col-md-6">
                      <label class="form-label dq-label">Status</label>
                      <select
                        v-model="form.status"
                        class="form-select dq-input"
                      >
                        <option>Active</option>
                        <option>Inactive</option>
                      </select>
                    </div>
                    <div class="col-md-6">
                      <div
                        class="dq-status"
                        :class="
                          form.status === 'Active'
                            ? 'dq-status-won'
                            : 'dq-status-lost'
                        "
                      >
                        <i
                          class="ti"
                          :class="
                            form.status === 'Active' ? 'ti-check' : 'ti-x'
                          "
                        ></i>
                        <span>{{
                          form.status === "Active"
                            ? "Pipeline is active"
                            : "Pipeline is inactive"
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-light dq-btn dq-btn-muted"
              data-bs-dismiss="modal"
              :disabled="saving"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="saving"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Add Pipeline
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <!-- /Add Pipeline -->

  <!-- Edit Pipeline -->
  <div class="modal fade dq-pipeline-modal" id="edit_pipeline" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-xl dq-pipeline-dialog">
      <div class="modal-content dq-modal-content" :key="editKey">
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-violet">
              <i class="ti ti-edit"></i>
            </div>
            <div class="dq-headtext">
              <h4 class="modal-title dq-title mb-0">Edit Pipeline</h4>
              <div class="dq-subtitle">
                Update stages, access and status. Changes apply immediately.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitEdit">
          <div class="modal-body dq-modal-body">
            <div class="row g-3">
              <!-- Details + Stages -->
              <div class="col-12">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <i class="ti ti-sparkles"></i> Pipeline Details
                    </div>
                    <div class="dq-card-sub">
                      Update pipeline name and stages.
                    </div>
                  </div>

                  <div class="row g-3 mt-0">
                    <div class="col-12">
                      <label class="form-label dq-label">
                        Pipeline Name <span class="text-danger">*</span>
                      </label>
                      <input
                        v-model.trim="form.name"
                        type="text"
                        class="form-control dq-input"
                        required
                      />
                    </div>

                    <div class="col-12">
                      <div
                        class="d-flex justify-content-between align-items-center mb-2"
                      >
                        <label class="form-label dq-label mb-0">
                          Pipeline Stages <span class="text-danger">*</span>
                        </label>

                        <button
                          type="button"
                          class="btn btn-outline-primary btn-sm dq-mini-btn"
                          @click="openStageModal"
                        >
                          <i class="ti ti-plus me-1"></i> Add Stage
                        </button>
                      </div>

                      <div v-if="form.stages.length" class="dq-stage-list">
                        <div
                          v-for="(st, idx) in form.stages"
                          :key="`e-${idx}-${
                            typeof st === 'string' ? st : st.name
                          }`"
                          class="dq-stage-row"
                        >
                          <div class="dq-stage-left">
                            <span class="dq-drag"
                              ><i class="ti ti-grip-vertical"></i
                            ></span>
                            <div class="dq-stage-meta">
                              <div class="dq-stage-name">
                                {{ typeof st === "string" ? st : st.name }}
                              </div>
                              <div class="dq-stage-sub">
                                Step {{ idx + 1 }} • Sort
                                {{
                                  typeof st === "object"
                                    ? st.sort_order ?? idx
                                    : idx
                                }}
                              </div>
                            </div>
                          </div>

                          <div class="dq-stage-actions">
                            <button
                              type="button"
                              class="btn btn-light btn-sm dq-iconbtn"
                              @click="openEditStage(idx, st)"
                              title="Edit"
                            >
                              <i class="ti ti-edit"></i>
                            </button>
                            <button
                              type="button"
                              class="btn btn-light btn-sm dq-iconbtn"
                              @click="removeStage(idx)"
                              title="Remove"
                            >
                              <i class="ti ti-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div v-else class="dq-empty">
                        <i class="ti ti-info-circle"></i>
                        <div>
                          No stages yet. Click <strong>Add Stage</strong> to
                          create the first step.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Access -->
              <div class="col-12">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <i class="ti ti-eye"></i> Access
                    </div>
                    <div class="dq-card-sub">
                      Choose who can use this pipeline.
                    </div>
                  </div>

                  <div class="dq-visibility">
                    <label class="dq-radio">
                      <input
                        class="form-check-input"
                        type="radio"
                        :checked="form.is_public"
                        @change="setPublic(true)"
                      />
                      <span class="dq-radio-box">
                        <i class="ti ti-world"></i>
                        <span class="dq-radio-title">All</span>
                      </span>
                    </label>

                    <label class="dq-radio">
                      <input
                        class="form-check-input"
                        type="radio"
                        :checked="!form.is_public"
                        @change="setPublic(false)"
                      />
                      <span class="dq-radio-box">
                        <i class="ti ti-users"></i>
                        <span class="dq-radio-title">Select People</span>
                      </span>
                    </label>
                  </div>

                  <div v-if="!form.is_public" class="dq-restrict mt-3">
                    <div class="dq-restrict-head">
                      <div class="dq-restrict-title">
                        <i class="ti ti-user-plus"></i> Choose people
                      </div>
                      <div class="dq-restrict-sub">
                        Search and add users who can access this pipeline.
                      </div>
                    </div>

                    <div v-if="accessSelected.length" class="dq-selected mt-2">
                      <span class="dq-mini">Selected:</span>
                      <span
                        v-for="u in accessSelected"
                        :key="'sel-e-' + u.id"
                        class="dq-chip"
                      >
                        <i class="ti ti-user"></i>
                        {{ u.label }}
                        <i
                          class="ti ti-x ms-1"
                          role="button"
                          @click="removeAccessUser(u.id)"
                        ></i>
                      </span>
                    </div>

                    <div
                      class="dropdown w-100 mt-2"
                      data-bs-auto-close="outside"
                      data-bs-display="static"
                    >
                      <button
                        class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                        type="button"
                        data-bs-toggle="dropdown"
                        @click="openAccessDropdown"
                      >
                        <span class="text-truncate">Add people…</span>
                        <i class="ti ti-chevron-down ms-2"></i>
                      </button>

                      <div class="dropdown-menu p-2 w-100 shadow">
                        <input
                          v-model="accessQuery"
                          type="text"
                          class="form-control mb-2"
                          placeholder="Search users…"
                          autocomplete="off"
                        />

                        <div class="search-list">
                          <div
                            v-if="accessLoading"
                            class="text-center py-2 small text-muted"
                          >
                            Loading…
                          </div>

                          <button
                            v-for="opt in accessOptions"
                            :key="'acc-e-' + opt.id"
                            type="button"
                            class="dropdown-item d-flex justify-content-between align-items-center"
                            @click="toggleAccessUser(opt)"
                          >
                            <span class="text-truncate">{{ opt.label }}</span>
                            <i
                              class="ti"
                              :class="
                                form.user_ids.includes(opt.id)
                                  ? 'ti-check'
                                  : 'ti-user-plus'
                              "
                            ></i>
                          </button>

                          <div
                            v-if="!accessLoading && !accessOptions.length"
                            class="text-center py-2 small text-muted"
                          >
                            No results
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Status -->
              <div class="col-12">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <i class="ti ti-activity"></i> Status
                    </div>
                    <div class="dq-card-sub">
                      Enable or disable this pipeline.
                    </div>
                  </div>

                  <div class="row g-3 mt-0">
                    <div class="col-md-6">
                      <label class="form-label dq-label">Status</label>
                      <select
                        v-model="form.status"
                        class="form-select dq-input"
                      >
                        <option>Active</option>
                        <option>Inactive</option>
                      </select>
                    </div>
                    <div class="col-md-6">
                      <div
                        class="dq-status"
                        :class="
                          form.status === 'Active'
                            ? 'dq-status-won'
                            : 'dq-status-lost'
                        "
                      >
                        <i
                          class="ti"
                          :class="
                            form.status === 'Active' ? 'ti-check' : 'ti-x'
                          "
                        ></i>
                        <span>{{
                          form.status === "Active"
                            ? "Pipeline is active"
                            : "Pipeline is inactive"
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-light dq-btn dq-btn-muted"
              data-bs-dismiss="modal"
              :disabled="saving"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="saving || !editItem"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Save Changes
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <!-- /Edit Pipeline -->

  <!-- Add Stage (child modal; no backdrop to avoid dim bug) -->
  <div class="modal fade nested dq-stage-modal" id="add_stage" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md">
      <div class="modal-content dq-modal-content" :key="stageAddKey">
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-emerald">
              <i class="ti ti-plus"></i>
            </div>
            <div class="dq-headtext">
              <h4 class="modal-title dq-title mb-0">Add New Stage</h4>
              <div class="dq-subtitle">
                Stages define how leads move through this pipeline.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            @click="hideStage('add_stage')"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="confirmAddStage">
          <div class="modal-body dq-modal-body">
            <label class="form-label dq-label">
              Stage Name <span class="text-danger">*</span>
            </label>
            <input
              v-model.trim="stageName"
              type="text"
              class="form-control dq-input"
              placeholder="e.g. Qualification"
            />
            <div class="dq-help mt-2">Keep it short and action-oriented.</div>
          </div>

          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-light dq-btn dq-btn-muted"
              @click="hideStage('add_stage')"
            >
              Cancel
            </button>
            <button type="submit" class="btn btn-primary dq-btn dq-btn-primary">
              Add Stage <i class="ti ti-plus ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Edit Stage (child modal; no backdrop) -->
  <div class="modal fade nested dq-stage-modal" id="edit_stage" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md">
      <div class="modal-content dq-modal-content" :key="stageEditKey">
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-amber">
              <i class="ti ti-edit"></i>
            </div>
            <div class="dq-headtext">
              <h4 class="modal-title dq-title mb-0">Edit Stage</h4>
              <div class="dq-subtitle">
                Update stage name — order remains consistent.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            @click="hideStage('edit_stage')"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="confirmEditStage">
          <div class="modal-body dq-modal-body">
            <label class="form-label dq-label">
              Edit Name <span class="text-danger">*</span>
            </label>
            <input
              v-model.trim="stageEditName"
              type="text"
              class="form-control dq-input"
              placeholder="Stage name"
            />
          </div>

          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-light dq-btn dq-btn-muted"
              @click="hideStage('edit_stage')"
            >
              Cancel
            </button>
            <button type="submit" class="btn btn-primary dq-btn dq-btn-primary">
              Save Changes <i class="ti ti-check ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete -->
  <div class="modal fade dq-pipeline-modal" id="delete_pipeline" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-sm dq-del-dialog">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header dq-danger-head">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-red">
              <i class="ti ti-trash"></i>
            </div>
            <div>
              <h5 class="modal-title dq-title mb-0">Confirm Delete</h5>
              <div class="dq-subtitle">This action cannot be undone.</div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body dq-modal-body">
          <div class="dq-danger">
            <i class="ti ti-alert-octagon"></i>
            <div>
              <div class="dq-danger-title">Delete this pipeline?</div>
              <div class="dq-danger-sub">
                Deals/Leads assigned to this pipeline may be affected.
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer dq-modal-footer">
          <button
            type="button"
            class="btn btn-light dq-btn dq-btn-muted"
            data-bs-dismiss="modal"
            :disabled="saving"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-danger dq-btn dq-btn-danger"
            :disabled="saving"
            @click="confirmDelete"
          >
            <span
              v-if="saving"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            Delete <i class="ti ti-trash ms-2"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import Modal from "bootstrap/js/dist/modal";
import { toast } from "@/utils/toast";
import { useUserStore } from "@/stores/userStore";
import { usePipelineStore } from "@/stores/pipelineStore";

const props = defineProps({ editItem: { type: Object, default: null } });
const emit = defineEmits(["saved"]);

const userStore = useUserStore();
const pipeStore = usePipelineStore();

const parentModal = ref(null);

const suppressResetOnNextShowAdd = ref(false);
const suppressResetOnNextShowEdit = ref(false);

const saving = computed(() => pipeStore.loading);

// UI keys
const addKey = ref(0);
const editKey = ref(0);
const stageAddKey = ref(0);
const stageEditKey = ref(0);

const form = ref({
  name: "",
  status: "Active",
  stages: [],
  is_public: true,
  user_ids: [],
});

// ------- People dropdown (Deals pattern) -------
const accessQuery = ref("");
const accessLoading = ref(false);

const people = computed(() => {
  const raw = Array.isArray(userStore.users)
    ? userStore.users
    : Array.isArray(userStore.users?.data)
    ? userStore.users.data
    : [];
  return raw;
});

const accessOptions = computed(() => {
  const q = accessQuery.value.trim().toLowerCase();
  return people.value
    .map((u) => ({
      id: Number(u.id),
      label: displayName(u),
    }))
    .filter((x) => (q ? x.label.toLowerCase().includes(q) : true));
});

const accessSelected = computed(() =>
  accessOptions.value.filter((x) => form.value.user_ids.includes(x.id))
);

async function openAccessDropdown() {
  accessLoading.value = true;
  try {
    if (!userStore.users?.length)
      await userStore.fetchUsers({ per_page: 1000 });
  } finally {
    accessLoading.value = false;
  }
}

function toggleAccessUser(opt) {
  const idx = form.value.user_ids.indexOf(opt.id);
  if (idx === -1) form.value.user_ids.push(opt.id);
  else form.value.user_ids.splice(idx, 1);
}
function removeAccessUser(id) {
  form.value.user_ids = form.value.user_ids.filter(
    (x) => Number(x) !== Number(id)
  );
}

// ------- Helpers: modals -------
function getInstance(id) {
  const el = document.getElementById(id);
  return el ? Modal.getInstance(el) || Modal.getOrCreateInstance(el) : null;
}
function isShown(id) {
  const el = document.getElementById(id);
  return !!el && el.classList.contains("show");
}

function openStageModal() {
  if (isShown("add_pipeline")) {
    parentModal.value = "add";
    suppressResetOnNextShowAdd.value = true;
    document
      .getElementById("add_pipeline")
      ?.addEventListener("hidden.bs.modal", showStageOnce, { once: true });
    getInstance("add_pipeline")?.hide();
    return;
  }
  if (isShown("edit_pipeline")) {
    parentModal.value = "edit";
    suppressResetOnNextShowEdit.value = true;
    document
      .getElementById("edit_pipeline")
      ?.addEventListener("hidden.bs.modal", showStageOnce, { once: true });
    getInstance("edit_pipeline")?.hide();
    return;
  }
  showStageOnce();
}

function showStageOnce() {
  getInstance("add_stage")?.show();
  setTimeout(() => {
    document.querySelector("#add_stage input")?.focus();
  }, 150);
}

// ------- Stage modals -------
const stageName = ref("");
const stageEditIndex = ref(null);
const stageEditName = ref("");

function reindexSort() {
  form.value.stages.forEach((s, i) => (s.sort_order = i));
}

function confirmAddStage() {
  const n = stageName.value.trim();
  if (!n) return toast("Stage name is required", "error");

  form.value.stages.push({ name: n, sort_order: form.value.stages.length });
  reindexSort();

  toast("Stage Added!");
  stageName.value = "";
  hideStage("add_stage");
  stageAddKey.value++;
}

function openEditStage(idx, st) {
  stageEditIndex.value = idx;
  stageEditName.value = typeof st === "string" ? st : st?.name || "";
  nextTick(() => {
    const el = document.getElementById("edit_stage");
    Modal.getOrCreateInstance(el, {
      backdrop: false,
      keyboard: true,
      focus: true,
    }).show();
  });
}

function confirmEditStage() {
  const n = stageEditName.value.trim();
  if (!n) return toast("Stage name is required", "error");

  if (stageEditIndex.value !== null) {
    const prev = form.value.stages[stageEditIndex.value] || {};
    form.value.stages.splice(stageEditIndex.value, 1, {
      id: prev.id,
      name: n,
      sort_order: prev.sort_order ?? stageEditIndex.value,
    });
    reindexSort();
  }

  stageEditIndex.value = null;
  stageEditName.value = "";
  hideStage("edit_stage");
  stageEditKey.value++;
}

function hideStage(id) {
  const el = document.getElementById(id);
  const m = el
    ? Modal.getInstance(el) ||
      Modal.getOrCreateInstance(el, { backdrop: false })
    : null;
  m?.hide();
}

// ------- Basic helpers -------
function displayName(u) {
  return (
    u.name ||
    `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() ||
    u.email ||
    `User ${u.id}`
  );
}

function setPublic(val) {
  form.value.is_public = !!val;
  if (val) form.value.user_ids = [];
}

function removeStage(idx) {
  form.value.stages.splice(idx, 1);
  reindexSort();
}

// ------- Reset / Hydrate -------
function blankForm() {
  return {
    name: "",
    status: "Active",
    stages: [],
    is_public: true,
    user_ids: [],
  };
}
function resetForm() {
  Object.assign(form.value, blankForm());
}

function hydrateFromEditItem(val) {
  if (!val) {
    resetForm();
    return;
  }
  form.value.name = val.name ?? "";
  form.value.status = val.status ?? "Active";
  form.value.stages = Array.isArray(val.stages)
    ? val.stages.map((s, i) => ({
        id: s.id ?? s?.pivot?.id,
        name: s.name ?? String(s).trim(),
        sort_order: s.sort_order ?? i,
      }))
    : (val.stages?.split?.(",") || []).map((n, i) => ({
        name: String(n).trim(),
        sort_order: i,
      }));
  reindexSort();

  form.value.is_public = val.is_public ?? true;
  form.value.user_ids = Array.isArray(val.user_ids)
    ? val.user_ids.map((x) => Number(x))
    : [];
  editKey.value++;
}

watch(
  () => props.editItem,
  (val) => hydrateFromEditItem(val),
  { immediate: true }
);

// ------- Save payload -------
function toPayload(f) {
  return {
    name: f.name?.trim(),
    status: f.status || "Active",
    is_public: !!f.is_public,
    user_ids: f.is_public ? [] : [...f.user_ids],
    stages: f.stages
      .map((s, i) => {
        if (typeof s === "string") return { name: s.trim(), sort_order: i };
        return {
          id: s.id,
          name: String(s.name || "").trim(),
          sort_order: Number.isInteger(s.sort_order) ? s.sort_order : i,
        };
      })
      .filter((s) => s.name),
  };
}

async function submitCreate() {
  if (!form.value.name?.trim())
    return toast("Pipeline name is required", "error");
  if (!form.value.stages.length)
    return toast("Add at least one stage", "error");
  if (!form.value.is_public && !form.value.user_ids.length)
    return toast("Select at least one user or choose All", "error");

  try {
    await pipeStore.create(toPayload(form.value));
    hideModal("add_pipeline");
    resetForm();
    addKey.value++;
    emit("saved");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to create pipeline", "error");
  }
}

async function submitEdit() {
  if (!props.editItem?.id) return;
  if (!form.value.name?.trim())
    return toast("Pipeline name is required", "error");
  if (!form.value.stages.length)
    return toast("Add at least one stage", "error");
  if (!form.value.is_public && !form.value.user_ids.length)
    return toast("Select at least one user or choose All", "error");

  try {
    await pipeStore.update(props.editItem.id, toPayload(form.value));
    hideModal("edit_pipeline");
    emit("saved");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to update pipeline", "error");
  }
}

function hideModal(id) {
  getInstance(id)?.hide();
}

async function confirmDelete() {
  try {
    if (!props.editItem?.id) return;
    await pipeStore.remove(props.editItem.id);
    hideModal("delete_pipeline");
    emit("saved");
  } catch (e) {
    console.error(e);
  }
}

// ------- Mount: nested modal restore guards -------
onMounted(async () => {
  if (!userStore.users?.length) await userStore.fetchUsers({ per_page: 1000 });

  const addEl = document.getElementById("add_pipeline");
  const editEl = document.getElementById("edit_pipeline");
  const stageEl = document.getElementById("add_stage");
  const editStageEl = document.getElementById("edit_stage");

  addEl?.addEventListener("show.bs.modal", () => {
    if (suppressResetOnNextShowAdd.value) {
      suppressResetOnNextShowAdd.value = false;
      return;
    }
    resetForm();
    addKey.value++;
  });

  editEl?.addEventListener("show.bs.modal", () => {
    if (suppressResetOnNextShowEdit.value) {
      suppressResetOnNextShowEdit.value = false;
      return;
    }
  });

  stageEl?.addEventListener("hidden.bs.modal", () => {
    if (parentModal.value === "add") getInstance("add_pipeline")?.show();
    else if (parentModal.value === "edit") getInstance("edit_pipeline")?.show();
    parentModal.value = null;
  });

  editStageEl?.addEventListener("hidden.bs.modal", () => {
    if (parentModal.value === "add") getInstance("add_pipeline")?.show();
    else if (parentModal.value === "edit") getInstance("edit_pipeline")?.show();
    parentModal.value = null;
  });

  addEl?.addEventListener("hidden.bs.modal", () => {
    if (parentModal.value === "add" || suppressResetOnNextShowAdd.value) return;
    resetForm();
  });

  editEl?.addEventListener("hidden.bs.modal", () => {
    if (parentModal.value === "edit" || suppressResetOnNextShowEdit.value)
      return;
    suppressResetOnNextShowEdit.value = false;
  });
});
</script>

<style scoped>
/* Nested modals ride above the parent while sharing the same backdrop */
.modal.nested {
  z-index: 1060; /* parent default is 1055 */
}

/* Dialog sizes */
.dq-pipeline-dialog {
  max-width: 1180px;
}
.dq-del-dialog {
  max-width: 540px;
}
@media (max-width: 992px) {
  .dq-pipeline-dialog,
  .dq-del-dialog {
    max-width: 96%;
  }
}

/* Modal shell */
.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 90px rgba(15, 23, 42, 0.14);
}

.dq-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(139, 92, 246, 0.14), #fff);
}
.dq-danger-head {
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.12), #fff);
}

.dq-modal-body {
  padding: 18px 22px;
  max-height: calc(100vh - 230px);
  overflow: auto;
}

.dq-modal-footer {
  padding: 14px 22px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 2;
}

/* Header */
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero */
.dq-hero {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-violet {
  background: rgba(139, 92, 246, 0.16);
  color: rgb(109, 40, 217);
}
.dq-hero-emerald {
  background: rgba(16, 185, 129, 0.16);
  color: rgb(5, 150, 105);
}
.dq-hero-amber {
  background: rgba(245, 158, 11, 0.16);
  color: rgb(217, 119, 6);
}
.dq-hero-red {
  background: rgba(239, 68, 68, 0.14);
  color: rgb(220, 38, 38);
}

/* Cards */
.dq-card {
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  padding: 14px;
}
.dq-card-head {
  padding: 2px 2px 10px;
}
.dq-card-title {
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.dq-card-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}

/* Inputs */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
}
.dq-input:focus {
  border-color: rgba(139, 92, 246, 0.55);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.14);
}
.dq-help {
  margin-top: 6px;
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
}

/* Stages */
.dq-stage-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.dq-stage-row {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 16px;
  padding: 12px;
  background: rgba(15, 23, 42, 0.02);
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.dq-stage-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.dq-drag {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-stage-meta {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.dq-stage-name {
  font-weight: 950;
}
.dq-stage-sub {
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.6);
  margin-top: 2px;
}
.dq-stage-actions {
  display: inline-flex;
  gap: 8px;
}
.dq-iconbtn {
  border-radius: 12px;
}
.dq-mini-btn {
  border-radius: 999px;
  font-weight: 900;
}

/* Empty state */
.dq-empty {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px;
  border-radius: 16px;
  border: 1px dashed rgba(15, 23, 42, 0.14);
  background: rgba(15, 23, 42, 0.02);
  color: rgba(15, 23, 42, 0.7);
}

/* Chips */
.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  padding: 8px 12px;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
  font-weight: 900;
  font-size: 0.82rem;
}
.dq-chip i.ti-x {
  cursor: pointer;
  opacity: 0.7;
}
.dq-chip i.ti-x:hover {
  opacity: 1;
}

.dq-mini {
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.55);
  font-weight: 900;
}
.dq-selected {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

/* Visibility */
.dq-visibility {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
}
@media (max-width: 992px) {
  .dq-visibility {
    grid-template-columns: 1fr;
  }
}
.dq-radio {
  cursor: pointer;
  user-select: none;
  display: block;
}
.dq-radio input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.dq-radio-box {
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 10px;
  align-items: start;
  padding: 12px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.02);
  transition: all 0.15s ease;
}
.dq-radio-box i {
  margin-top: 2px;
  font-size: 18px;
  color: rgba(15, 23, 42, 0.7);
}
.dq-radio-title {
  font-weight: 950;
}
.dq-radio input:checked + .dq-radio-box {
  border-color: rgba(139, 92, 246, 0.35);
  background: rgba(139, 92, 246, 0.08);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.12);
}
.dq-radio input:checked + .dq-radio-box i {
  color: rgb(109, 40, 217);
}

/* Restrict box */
.dq-restrict {
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
  padding: 12px;
}
.dq-restrict-title {
  font-weight: 950;
  display: inline-flex;
  gap: 8px;
  align-items: center;
}
.dq-restrict-sub {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}

/* Dropdown list */
.search-list {
  max-height: 260px;
  overflow: auto;
  border-radius: 12px;
}
.dropdown-menu.w-100 {
  min-width: 100% !important;
}
.dropdown-item {
  border-radius: 10px;
}
.dropdown-item:active {
  background: rgba(139, 92, 246, 0.12);
}

/* Buttons */
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 900;
}
.dq-btn-muted {
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}
.dq-btn-danger {
  box-shadow: 0 12px 24px rgba(239, 68, 68, 0.12);
}

/* Status pill */
.dq-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 14px;
  font-size: 0.82rem;
  font-weight: 800;
  border: 1px solid rgba(15, 23, 42, 0.06);
  margin-top: 28px;
}
.dq-status-won {
  background: rgba(16, 185, 129, 0.12);
  color: rgba(5, 150, 105, 1);
}
.dq-status-lost {
  background: rgba(239, 68, 68, 0.12);
  color: rgba(220, 38, 38, 1);
}

/* Delete warning */
.dq-danger {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 14px;
  border-radius: 18px;
  border: 1px solid rgba(239, 68, 68, 0.14);
  background: rgba(239, 68, 68, 0.08);
}
.dq-danger i {
  font-size: 18px;
  color: rgb(220, 38, 38);
  margin-top: 2px;
}
.dq-danger-title {
  font-weight: 950;
}
.dq-danger-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.7);
}
</style>
